/**
 * IndexedDB 缓存实现
 * 支持数据的存储、获取、清理，默认缓存时间为10分钟
 */

import { addBreadcrumb, captureError } from '@/utils'

interface CacheItem<T = any> {
  data: T
  timestamp: number
  expireAt: number
}

class IndexedDBCache {
  private dbName = 'api-cache'
  private version = 1
  private storeName = 'api-cache'
  private db: IDBDatabase | null = null
  private defaultTTL = 10 * 60 * 1000 // 10分钟，单位毫秒

  constructor() {
    this.initDB()
  }

  /**
   * 初始化 IndexedDB
   */
  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version)

      request.onerror = () => {
        const dbError = request.error ?? new Error('IndexedDB 打开失败')
        addBreadcrumb({
          message: 'IndexedDB 打开失败',
          category: 'api/cache',
          level: 'error',
          data: {
            errorMessage: dbError.message,
            errorName: dbError.name
          }
        })
        captureError(dbError, {
          feature: 'indexedDB-cache',
          action: 'open',
          stage: 'initDB'
        })
        reject(dbError)
      }

      request.onsuccess = () => {
        this.db = request.result
        addBreadcrumb({
          message: 'IndexedDB 初始化成功',
          category: 'api/cache',
          level: 'info'
        })
        resolve()
      }

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result

        // 创建对象存储空间
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' })
          store.createIndex('expireAt', 'expireAt', { unique: false })
          store.createIndex('key', 'key', { unique: true })
        }
      }
    })
  }

  /**
   * 确保数据库已初始化
   */
  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.initDB()
    }
    if (!this.db) {
      throw new Error('数据库初始化失败')
    }
    return this.db
  }

  /**
   * 设置缓存数据
   * @param key 缓存键
   * @param data 要缓存的数据
   * @param ttl 过期时间（毫秒），默认10分钟
   */
  async set<T>(
    key: string,
    data: T,
    ttl: number = this.defaultTTL
  ): Promise<void> {
    try {
      const db = await this.ensureDB()
      const transaction = db.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)

      const now = Date.now()
      const cacheItem: CacheItem<T> & { key: string } = {
        key,
        data,
        timestamp: now,
        expireAt: now + ttl
      }

      return new Promise((resolve, reject) => {
        const request = store.put(cacheItem)

        request.onsuccess = () => {
          addBreadcrumb({
            message: '缓存已设置',
            category: 'api/cache',
            level: 'info',
            data: { key, ttl }
          })
          resolve()
        }

        request.onerror = () => {
          const dbError = request.error ?? new Error('缓存设置失败')
          addBreadcrumb({
            message: '缓存设置失败',
            category: 'api/cache',
            level: 'error',
            data: {
              key,
              errorMessage: dbError.message
            }
          })
          captureError(dbError, {
            feature: 'indexedDB-cache',
            action: 'set',
            key,
            ttl
          })
          reject(dbError)
        }
      })
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      addBreadcrumb({
        message: '设置缓存时发生错误',
        category: 'api/cache',
        level: 'error',
        data: {
          key,
          errorMessage: err.message
        }
      })
      captureError(err, {
        feature: 'indexedDB-cache',
        action: 'set',
        key,
        ttl
      })
      throw err
    }
  }

  /**
   * 获取缓存数据
   * @param key 缓存键
   * @returns 缓存的数据，如果不存在或已过期则返回 null
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const db = await this.ensureDB()
      const transaction = db.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)

      return new Promise((resolve, reject) => {
        const request = store.get(key)

        request.onsuccess = () => {
          const result = request.result as
            | (CacheItem<T> & { key: string })
            | undefined

          if (!result) {
            addBreadcrumb({
              message: '缓存未找到',
              category: 'api/cache',
              level: 'info',
              data: { key }
            })
            resolve(null)
            return
          }

          const now = Date.now()

          // 检查是否过期
          if (now > result.expireAt) {
            addBreadcrumb({
              message: '缓存已过期',
              category: 'api/cache',
              level: 'warning',
              data: {
                key,
                expireAt: result.expireAt,
                now
              }
            })
            // 异步删除过期数据
            this.delete(key).catch(err => {
              const deleteError =
                err instanceof Error ? err : new Error(String(err))
              addBreadcrumb({
                message: '删除过期缓存失败',
                category: 'api/cache',
                level: 'error',
                data: {
                  key,
                  errorMessage: deleteError.message
                }
              })
              captureError(deleteError, {
                feature: 'indexedDB-cache',
                action: 'delete-expired',
                key
              })
            })
            resolve(null)
            return
          }

          addBreadcrumb({
            message: '缓存命中',
            category: 'api/cache',
            level: 'info',
            data: { key }
          })
          resolve(result.data)
        }

        request.onerror = () => {
          const dbError = request.error ?? new Error('获取缓存失败')
          addBreadcrumb({
            message: '获取缓存失败',
            category: 'api/cache',
            level: 'error',
            data: {
              key,
              errorMessage: dbError.message
            }
          })
          captureError(dbError, {
            feature: 'indexedDB-cache',
            action: 'get',
            key
          })
          reject(dbError)
        }
      })
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      addBreadcrumb({
        message: '获取缓存时发生错误',
        category: 'api/cache',
        level: 'error',
        data: {
          key,
          errorMessage: err.message
        }
      })
      captureError(err, {
        feature: 'indexedDB-cache',
        action: 'get',
        key
      })
      return null
    }
  }

  /**
   * 删除指定缓存
   * @param key 缓存键
   */
  async delete(key: string): Promise<void> {
    try {
      const db = await this.ensureDB()
      const transaction = db.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)

      return new Promise((resolve, reject) => {
        const request = store.delete(key)

        request.onsuccess = () => {
          addBreadcrumb({
            message: '缓存已删除',
            category: 'api/cache',
            level: 'info',
            data: { key }
          })
          resolve()
        }

        request.onerror = () => {
          const dbError = request.error ?? new Error('删除缓存失败')
          addBreadcrumb({
            message: '删除缓存失败',
            category: 'api/cache',
            level: 'error',
            data: {
              key,
              errorMessage: dbError.message
            }
          })
          captureError(dbError, {
            feature: 'indexedDB-cache',
            action: 'delete',
            key
          })
          reject(dbError)
        }
      })
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      addBreadcrumb({
        message: '删除缓存时发生错误',
        category: 'api/cache',
        level: 'error',
        data: {
          key,
          errorMessage: err.message
        }
      })
      captureError(err, {
        feature: 'indexedDB-cache',
        action: 'delete',
        key
      })
      throw err
    }
  }

  /**
   * 清理缓存
   * @param key 可选，指定键则删除特定缓存，否则清理所有缓存
   */
  async clear(key?: string): Promise<void> {
    try {
      if (key) {
        // 删除指定缓存
        await this.delete(key)
        return
      }

      // 清理所有缓存
      const db = await this.ensureDB()
      const transaction = db.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)

      return new Promise((resolve, reject) => {
        const request = store.clear()

        request.onsuccess = () => {
          addBreadcrumb({
            message: '所有缓存已清理',
            category: 'api/cache',
            level: 'info'
          })
          resolve()
        }

        request.onerror = () => {
          const dbError = request.error ?? new Error('清理缓存失败')
          addBreadcrumb({
            message: '清理缓存失败',
            category: 'api/cache',
            level: 'error',
            data: {
              errorMessage: dbError.message
            }
          })
          captureError(dbError, {
            feature: 'indexedDB-cache',
            action: 'clear-all'
          })
          reject(dbError)
        }
      })
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      addBreadcrumb({
        message: '清理缓存时发生错误',
        category: 'api/cache',
        level: 'error',
        data: {
          errorMessage: err.message,
          key
        }
      })
      captureError(err, {
        feature: 'indexedDB-cache',
        action: 'clear',
        key
      })
      throw err
    }
  }

  /**
   * 清理过期缓存
   */
  async clearExpired(): Promise<void> {
    try {
      const db = await this.ensureDB()
      const transaction = db.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const index = store.index('expireAt')

      const now = Date.now()
      const range = IDBKeyRange.upperBound(now)

      return new Promise((resolve, reject) => {
        const request = index.openCursor(range)
        let deletedCount = 0

        request.onsuccess = event => {
          const cursor = (event.target as IDBRequest).result

          if (cursor) {
            cursor.delete()
            deletedCount++
            cursor.continue()
          } else {
            addBreadcrumb({
              message: '过期缓存已清理',
              category: 'api/cache',
              level: 'info',
              data: {
                deletedCount
              }
            })
            resolve()
          }
        }

        request.onerror = () => {
          const dbError = request.error ?? new Error('清理过期缓存失败')
          addBreadcrumb({
            message: '清理过期缓存失败',
            category: 'api/cache',
            level: 'error',
            data: {
              errorMessage: dbError.message
            }
          })
          captureError(dbError, {
            feature: 'indexedDB-cache',
            action: 'clear-expired'
          })
          reject(dbError)
        }
      })
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      addBreadcrumb({
        message: '清理过期缓存时发生错误',
        category: 'api/cache',
        level: 'error',
        data: {
          errorMessage: err.message
        }
      })
      captureError(err, {
        feature: 'indexedDB-cache',
        action: 'clear-expired'
      })
      throw err
    }
  }

  /**
   * 获取缓存统计信息
   */
  async getStats(): Promise<{ total: number; expired: number }> {
    try {
      const db = await this.ensureDB()
      const transaction = db.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)

      return new Promise((resolve, reject) => {
        const request = store.getAll()

        request.onsuccess = () => {
          const items = request.result as (CacheItem & { key: string })[]
          const now = Date.now()

          const stats = {
            total: items.length,
            expired: items.filter(item => now > item.expireAt).length
          }

          resolve(stats)
        }

        request.onerror = () => {
          const dbError = request.error ?? new Error('获取缓存统计失败')
          addBreadcrumb({
            message: '获取缓存统计失败',
            category: 'api/cache',
            level: 'error',
            data: {
              errorMessage: dbError.message
            }
          })
          captureError(dbError, {
            feature: 'indexedDB-cache',
            action: 'get-stats'
          })
          reject(dbError)
        }
      })
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      addBreadcrumb({
        message: '获取缓存统计时发生错误',
        category: 'api/cache',
        level: 'error',
        data: {
          errorMessage: err.message
        }
      })
      captureError(err, {
        feature: 'indexedDB-cache',
        action: 'get-stats'
      })
      throw err
    }
  }

  /**
   * 关闭数据库连接
   */
  close(): void {
    if (this.db) {
      this.db.close()
      this.db = null
      addBreadcrumb({
        message: 'IndexedDB 连接已关闭',
        category: 'api/cache',
        level: 'info'
      })
    }
  }
}

// 创建单例实例
const dbCache = new IndexedDBCache()

// 定期清理过期缓存（每小时执行一次）
if (typeof window !== 'undefined') {
  setInterval(
    () => {
      dbCache.clearExpired().catch(err => {
        const error = err instanceof Error ? err : new Error(String(err))
        addBreadcrumb({
          message: '定期清理过期缓存失败',
          category: 'api/cache',
          level: 'error',
          data: {
            errorMessage: error.message
          }
        })
        captureError(error, {
          feature: 'indexedDB-cache',
          action: 'auto-clear-expired'
        })
      })
    },
    60 * 60 * 1000
  ) // 1小时
}

export default dbCache

// 导出类型
export type { CacheItem }
